import { zodResolver } from '@hookform/resolvers/zod'
import {
  Captions,
  ChevronDown,
  Clock,
  Film,
  Flame,
  Gauge,
  Globe,
  Hash,
  Image as ImageIcon,
  Layers,
  Megaphone,
  MessageSquare,
  Minus,
  Plus,
  Sparkles,
  Wand2,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import {
  QualityTierSelector,
  type QualityTier,
} from '@/features/factory/components/QualityTierSelector'
import { SuggestPromptButton } from '@/features/factory/components/SuggestPromptButton'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  useFactoryCatalog,
  useStartViralShorts,
  useSuggestPrompt,
  type ViralShortsInput,
} from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { CaptionStyleCarousel } from '@/components/forms/CaptionStyleCarousel'
import { ThumbnailStyleCarousel } from '@/components/forms/ThumbnailStyleCarousel/ThumbnailStyleCarousel'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { CinemaBackground } from './CinemaBackground'
import { PresetBar } from '../_shared/PresetBar'
import {
  formToPayload,
  payloadToForm,
  type FormValues as SerializerFormValues,
} from './presetSerializer'
import styles from './ViralShortsForm.module.scss'

// Human labels for the backend fields. Keeps the 422 banner readable even
// if the user sends a malformed payload via devtools.
const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  custom_prompt: 'Prompt',
  end_text: 'CTA final',
  tone: 'Tom',
  default_video_duration: 'Duração',
  subtitle_style: 'Estilo de legenda',
  thumbnail_style: 'Estilo de thumbnail',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
  stock_source: 'Banco de vídeos',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

// Duration presets map to `default_video_duration` seconds on submit. The
// short and standard send exactly their label; long sends 75 s, the
// midpoint of its range.
// The seconds-mapping itself lives in ``./presetSerializer.ts`` (as
// ``DURATION_SECONDS``) so save-preset and submit share one source of truth.
type DurationKey = 'short' | 'standard' | 'long'
const DURATIONS: ReadonlyArray<SegmentedOption<DurationKey>> = [
  { value: 'short', label: '30s' },
  { value: 'standard', label: '45s' },
  { value: 'long', label: '60–90s' },
]

type ToneKey = 'formal' | 'curious' | 'sarcastic'
const TONES: ReadonlyArray<SegmentedOption<ToneKey>> = [
  { value: 'formal', label: 'Formal' },
  { value: 'curious', label: 'Curioso' },
  { value: 'sarcastic', label: 'Debochado' },
]

type StockSourceKey = 'pexels' | 'pixabay'
const STOCK_SOURCES: ReadonlyArray<SegmentedOption<StockSourceKey>> = [
  { value: 'pexels', label: 'Pexels' },
  { value: 'pixabay', label: 'Pixabay' },
]

const META_THEME_OPTIONS = [
  { value: 'random', label: 'Aleatório' },
  { value: 'custom', label: 'Prompt customizado' },
] as const

const schema = z
  .object({
    theme: z.string().min(1, 'Escolha um tema ou prompt'),
    count: z.number().int().min(1).max(10),
    language: z.enum(['pt-br', 'en', 'both']),
    duration: z.enum(['short', 'standard', 'long']),
    custom_prompt: z.string().trim().optional(),
    end_text: z.string().trim().optional(),
    tone: z.enum(['formal', 'curious', 'sarcastic']).optional(),
    subtitle_style: z.string().optional(),
    subtitle_active_color: z.string().optional(),
    thumbnail_style: z.string().optional(),
    tts_voice_pt: z.string().optional(),
    tts_voice_en: z.string().optional(),
    stock_source: z.enum(['pexels', 'pixabay']).optional(),
    llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
  })
  // Empty custom_prompt is valid — the backend auto-generates one via
  // suggest_prompt when the field is blank. Only reject strings that
  // are non-empty but too short to be a real prompt.
  .refine(
    (v) => {
      if (v.theme !== 'custom') return true
      const len = v.custom_prompt?.length ?? 0
      return len === 0 || len >= 10
    },
    {
      path: ['custom_prompt'],
      message: 'Descreva com pelo menos 10 caracteres, ou deixe em branco para gerar um automaticamente.',
    },
  )

type FormValues = z.infer<typeof schema>

export function ViralShortsForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: themes = [], isPending: themesPending } = useThemes()
  const startMutation = useStartViralShorts()
  const suggestPrompt = useSuggestPrompt()

  // Catalog drives the quality-tier selector visibility + cost estimate
  // (decisões 53–55). Passing ``channel`` activates the per-channel cost
  // aggregation; null while channel hasn't resolved yet returns the
  // global-defaults variant (still cached separately).
  const { data: catalog = [] } = useFactoryCatalog(channel || null)
  // ``factories.slug`` is hyphenated in the catalog row (``viral-shorts``)
  // even though the registry slug is underscored — match the DB shape.
  const viralEntry = catalog.find((c) => c.slug === 'viral-shorts')

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // Quality tier lives outside react-hook-form so it stays out of the
  // PresetBar payload (a saved preset never carries the tier — it always
  // resets on visit / "Gerar outro"). PresetBar reads ``form.watch()``
  // verbatim — keeping tier here means it never gets serialized into a
  // saved preset. Default is ``'high'`` for HQ-capable factories; the
  // effect below downgrades to ``'standard'`` if the catalog reports HQ
  // isn't supported for this factory (defensive — viral_shorts always
  // advertises 'high' today).
  const [qualityTier, setQualityTier] = useState<QualityTier>('high')
  useEffect(() => {
    if (viralEntry && !viralEntry.supported_tiers.includes('high')) {
      setQualityTier('standard')
    }
  }, [viralEntry])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      // ``random`` é o caminho mais provável — mantém o usuário no fluxo
      // sem ter que escolher tema toda vez. Pra theme específico, é só
      // trocar no Select.
      theme: 'random',
      count: 1,
      language: 'pt-br',
      duration: 'standard',
      custom_prompt: '',
      end_text: '',
      stock_source: 'pexels',
      llm_provider: 'auto',
    },
  })

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const duration = form.watch('duration')
  const tone = form.watch('tone')
  const subtitleStyle = form.watch('subtitle_style')
  const subtitleActiveColor = form.watch('subtitle_active_color')
  const thumbnailStyle = form.watch('thumbnail_style')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const stockSource = form.watch('stock_source') ?? 'pexels'
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    // Reuse the serializer that PresetBar uses for save → keeps the manual
    // run and the saved preset on the same payload shape. ``quality_tier``
    // is injected here (not in ``formToPayload``) so it stays out of the
    // preset's persisted JSON (decisão 57).
    const payload: ViralShortsInput = {
      channel,
      ...formToPayload(values as SerializerFormValues),
      quality_tier: qualityTier,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  /** Re-hydrate the form when PresetBar applies a preset chip. Tier is
   * intentionally NOT restored from the preset — the form always resets
   * to the factory's default tier (``'high'`` when HQ is supported) so
   * users reconfirm the cost tradeoff every time. */
  const handleApplyPreset = (values: SerializerFormValues) => {
    form.reset(values)
    setQualityTier(viralEntry?.supported_tiers.includes('high') ? 'high' : 'standard')
  }

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
              // Fresh form resets to the factory's default tier — HQ when
              // supported, standard otherwise.
              setQualityTier(viralEntry?.supported_tiers.includes('high') ? 'high' : 'standard')
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Flame aria-hidden /> Viral · trending
          </div>
          <h1 className={styles.title}>O que vai viralizar hoje?</h1>
          <p className={styles.subtitle}>
            Escolha um preset ou ajuste os parâmetros. Prism pesca o melhor tema do momento e gera.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<SerializerFormValues>
              factory="viral_shorts"
              channel={channel}
              formValues={form.watch() as SerializerFormValues}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={handleApplyPreset}
            />
          ) : null}
          <div className={styles.rows}>
        <Row icon={<Hash />} label="Tema" htmlFor="vs-theme">
          <SelectRoot
            value={theme}
            onValueChange={(v) => {
              form.setValue('theme', v, { shouldValidate: true })
            }}
          >
            <SelectTrigger id="vs-theme" aria-label="Tema">
              <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
            </SelectTrigger>
            <SelectContent>
              {META_THEME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
              {themes.map((t: Theme) => (
                <SelectItem key={t.slug} value={t.slug}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          {form.formState.errors.theme ? (
            <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
          ) : null}
        </Row>

        {theme === 'custom' ? (
          <Row icon={<Wand2 />} label="Prompt" htmlFor="vs-prompt">
            <textarea
              id="vs-prompt"
              className={cn(
                styles.textarea,
                form.formState.errors.custom_prompt && styles.textareaError,
              )}
              placeholder="Ex: Curiosidades sobre buracos negros que assustam"
              rows={3}
              {...form.register('custom_prompt')}
            />
            <SuggestPromptButton
              factory="viral_shorts"
              language={language}
              channel={channel}
              pending={suggestPrompt.isPending}
              error={suggestPrompt.error}
              onClick={() => {
                suggestPrompt.mutate(
                  { factory: 'viral_shorts', language, channel: channel || null },
                  {
                    onSuccess: (prompt) => {
                      form.setValue('custom_prompt', prompt, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    },
                  },
                )
              }}
            />
            {form.formState.errors.custom_prompt ? (
              <span className={styles.errorText}>
                {form.formState.errors.custom_prompt.message}
              </span>
            ) : null}
          </Row>
        ) : null}

        <Row icon={<Clock />} label="Duração">
          <Segmented<DurationKey>
            options={DURATIONS}
            value={duration}
            onValueChange={(v) => {
              form.setValue('duration', v)
            }}
            aria-label="Duração"
          />
        </Row>

        <Row icon={<Layers />} label="Quantidade">
          <div className={styles.stepper}>
            <IconButton
              aria-label="Diminuir"
              variant="ghost"
              size="sm"
              disabled={count <= 1}
              onClick={() => {
                form.setValue('count', Math.max(1, count - 1))
              }}
            >
              <Minus />
            </IconButton>
            <span className={styles.stepperValue}>{count}</span>
            <IconButton
              aria-label="Aumentar"
              variant="ghost"
              size="sm"
              disabled={count >= 10}
              onClick={() => {
                form.setValue('count', Math.min(10, count + 1))
              }}
            >
              <Plus />
            </IconButton>
          </div>
        </Row>

        {/* Decisão 53: QualityTierSelector renders in the main row,
            not the "Ajuste fino" collapsible — HQ is a conscious cost
            decision and hiding it would invisibilize the tradeoff. The
            component itself returns ``null`` when this factory's
            ``supported_tiers`` doesn't include ``'high'`` (decisão 54),
            so this Row collapses cleanly for opt-out factories. */}
        {viralEntry?.supported_tiers.includes('high') ? (
          <Row icon={<Gauge />} label="Qualidade">
            <QualityTierSelector
              value={qualityTier}
              onValueChange={setQualityTier}
              supportedTiers={viralEntry.supported_tiers}
              costEstimate={viralEntry.cost_estimate}
            />
          </Row>
        ) : null}

        {advancedOpen ? (
          <div className={styles.advanced}>
            <AdvSection icon={<Globe />} label="Idioma">
              <Segmented<'pt-br' | 'en' | 'both'>
                options={LANGUAGES}
                value={language}
                onValueChange={(v) => {
                  form.setValue('language', v)
                }}
                aria-label="Idioma"
              />
            </AdvSection>

            <AdvSection icon={<Megaphone />} label="CTA final">
              <Input
                id="vs-end-text"
                placeholder="Ex: Inscreva-se para mais!"
                {...form.register('end_text')}
              />
            </AdvSection>

            <AdvSection icon={<MessageSquare />} label="Tom">
              <Segmented<ToneKey>
                options={TONES}
                value={tone ?? ('' as ToneKey)}
                onValueChange={(v) => {
                  form.setValue('tone', v)
                }}
                aria-label="Tom"
              />
            </AdvSection>

            <AdvSection icon={<Captions />} label="Estilo de legenda">
              <CaptionStyleCarousel
                value={subtitleStyle ?? ''}
                onValueChange={(v) => {
                  form.setValue('subtitle_style', v)
                }}
                activeColor={subtitleActiveColor ?? ''}
                onActiveColorChange={(c) => {
                  form.setValue('subtitle_active_color', c)
                }}
              />
            </AdvSection>

            <AdvSection icon={<ImageIcon />} label="Estilo de thumbnail">
              <ThumbnailStyleCarousel
                value={thumbnailStyle ?? ''}
                onValueChange={(v) => {
                  form.setValue('thumbnail_style', v)
                }}
              />
            </AdvSection>

            <AdvSection icon={<Film />} label="Banco de vídeos">
              <Segmented<StockSourceKey>
                options={STOCK_SOURCES}
                value={stockSource}
                onValueChange={(v) => {
                  form.setValue('stock_source', v)
                }}
                aria-label="Banco de vídeos"
              />
            </AdvSection>

            <NarratorPicker
              language={language}
              voicePt={voicePt ?? ''}
              voiceEn={voiceEn ?? ''}
              onVoicePtChange={(v) => {
                form.setValue('tts_voice_pt', v)
              }}
              onVoiceEnChange={(v) => {
                form.setValue('tts_voice_en', v)
              }}
            />

            <LlmProviderPicker
              value={llmProvider ?? 'auto'}
              onChange={(v) => {
                form.setValue('llm_provider', v)
              }}
            />
          </div>
        ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino <ChevronDown aria-hidden />
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="curiosity" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

/** Row layout used inside the factory form: small accent icon + uppercase
 * mono label on the left, control on the right. Keeps the form visually
 * consistent without a separate component file. */
function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

/** Stacked subsection inside the advanced panel: full-width label + icon
 * on top, control below. Used for controls (carousels, segmented) that
 * need full panel width — the side-by-side `Row` layout would squeeze
 * them. */
function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}
